import clsx from 'clsx'
import useToggle from 'hooks/useToggle'
import { rem } from 'polished'
import React, { useCallback, useRef } from 'react'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import noop from 'lib/function/noop'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import TextLink from 'LuxKit/TextLink'
import { useElementHeight } from 'hooks/useElementSize'
import useHasMounted from 'hooks/useHasMounted'

const LargeContentWrapper = styled.div`
  position: relative;
  overflow: hidden;

  &.enable-transition {
    transition: max-height 0.2s;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: ${rem(32)};
    background: linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1));
    opacity: 0;
    transition: opacity 0.2s;
    pointer-events: none;
  }

  &.show-mask::after {
    opacity: 1;
  }
`

const SwitchButton = styled(TextLink)`
  margin-top: ${rem(8)};

  ${mediaQueryUp.tablet} {
    justify-self: left;
  }
`

interface Props {
  children: React.ReactNode
  maxHeight: number
  collapsedHeight?: number
  showMoreText?: string
  showLessText?: string
  showChevron?: boolean
  hideText?: boolean
  open?: boolean
  defaultOpen?: boolean
  onToggle?: () => void
  minimumContentHeight?: number
  /**
   * Font weight for the show more / show less toggle link.
   * @default 'bold'
   */
  toggleWeight?: 'regular' | 'bold'
}

function LargeContentToggle(props: Props) {
  const {
    defaultOpen,
    children,
    maxHeight,
    collapsedHeight = maxHeight,
    showMoreText,
    showLessText,
    hideText = false,
    showChevron = true,
    open,
    onToggle = noop,
    minimumContentHeight = 0,
    toggleWeight = 'bold',
  } = props
  const [showAll, toggleAll] = useToggle(defaultOpen)
  const contentElement = useRef<HTMLDivElement>(null)
  const isControlled = open !== undefined

  const onToggleClick = useCallback(() => {
    onToggle()
    if (!isControlled) {
      toggleAll()
    }
  }, [toggleAll, isControlled, onToggle])

  const contentHeight = useElementHeight(contentElement) ?? maxHeight

  const adjustedContentHeight = Math.max(minimumContentHeight, contentHeight)
  const isTaller = adjustedContentHeight > maxHeight
  const expanded = !isTaller || (isControlled ? open : showAll)

  const hasMounted = useHasMounted()

  return (<>
    <LargeContentWrapper
      className={clsx({ 'show-mask': !expanded, 'enable-transition': hasMounted })}
      style={{ maxHeight: expanded ? adjustedContentHeight : collapsedHeight }}
    >
      <div ref={contentElement}>
        {children}
      </div>
    </LargeContentWrapper>
    {!hideText && isTaller &&
      <SwitchButton
        size="medium"
        weight={toggleWeight}
        aria-expanded={expanded}
        endIcon={showChevron ? <DropdownChevron open={expanded} /> : undefined}
        onClick={onToggleClick}
      >
        {expanded ? showLessText : showMoreText}
      </SwitchButton>}
  </>)
}

export default LargeContentToggle
